/* Tip-style strategies (Hot tip, Normal tip, VIP offer, Paid plan): essentials
   first, the rest under Advanced. All edits go straight into the policy draft;
   the page autosaves it. */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { HookStyle, HotTipPolicyBody, NormalTipPolicyBody, PlacementRegime, SubOfferPolicyBody, VipOfferPolicyBody } from '../../../../api/client';
import { useSalesCatalogue } from '../../SalesLadder/useSalesCatalogue';
import { isVideoItem } from '../../SalesLadder/MediaBadge';
import { AdvancedToggle, Btn, Card, Foot, NumInput, Row, Section, Seg, Select, Stack, TextArea, TextInput, THead, Toggle, TRow } from '../kit';
import { font } from '../style';
import { HooksTable } from '../HooksTable';
import { MediaPicker } from '../MediaPicker';
import { ThumbStrip } from '../ThumbStrip';
import { useResolveIds } from '../resolveIds';
import { C } from '../tokens';
import { MAX_ASKS, MIN_ASK_TURN, PREVIEW_BARS, axisMax, barOf, barTurn, isCountPreset, phaseOf, resolveAsks, setArcSpan, setAskCount, setRegime, toggleBar, writeAsks } from './model';

export type Patch<T> = (p: Partial<T>) => void;
interface PageProps<T> { d: T; set: Patch<T>; adv: boolean; togAdv: () => void }

const Turns = ({ children }: { children: ReactNode }) => <>{children}<span>{useTranslation('assistantCompact').t('turns')}</span></>;

/* ── Shared Advanced sections ───────────────────────────────────── */

function HooksSection({ hooks, onChange, invite }: { hooks: HookStyle[]; onChange: (h: HookStyle[]) => void; invite?: boolean }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <Section title={t('mon.hooks')} hint={invite ? t('mon.hooksHintInvite') : t('mon.hooksHint')}>
      <HooksTable hooks={hooks} onChange={onChange} promptPlaceholder={t('mon.hookPh')} emptyText={t('mon.hooksEmpty')} />
    </Section>
  );
}

function AlertsSection({ decline, done, onDecline, onDone, doneLabel, doneHelp, doneDisabledHelp }: {
  decline: boolean; done: boolean; onDecline: (v: boolean) => void; onDone: (v: boolean) => void; doneLabel: string; doneHelp: string; doneDisabledHelp?: string;
}) {
  const { t } = useTranslation('assistantCompact');
  return (
    <Section title={t('mon.alerts')} hint={t('mon.alertsHint')}>
      <Card>
        <Row label={t('mon.notifyDecline')} help={t('mon.notifyDeclineHelp')}><Toggle on={decline} onChange={onDecline} /></Row>
        <Row label={doneLabel} help={doneDisabledHelp ?? doneHelp}><Toggle on={done && !doneDisabledHelp} onChange={onDone} disabled={!!doneDisabledHelp} /></Row>
      </Card>
    </Section>
  );
}

function GuidanceRow({ label, value, onChange, placeholder, hint, height = 60 }: { label: string; value: string | null; onChange: (v: string | null) => void; placeholder?: string; hint?: string; height?: number }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <Row stack label={label} help={hint ?? t('optional')}>
      <TextArea value={value ?? ''} onChange={(v) => onChange(v)} placeholder={placeholder} height={height} />
    </Row>
  );
}

const pct = (f: number | null | undefined) => (f === null || f === undefined ? null : Math.round(f * 100));

/* ── Hot tip ────────────────────────────────────────────────────── */

const REGIMES: { id: PlacementRegime; key: string }[] = [
  { id: 'beginning_only', key: 'startOnly' },
  { id: 'during', key: 'repeated' },
  { id: 'beginning_during', key: 'startDuring' },
  { id: 'beginning_end', key: 'startEnd' },
  { id: 'beginning_middle_end', key: 'startMidEnd' },
  { id: 'custom', key: 'custom' },
];
const ASK_COLS = '64px 56px minmax(0,1fr) 84px 128px';

function Placement({ d, set }: { d: HotTipPolicyBody; set: Patch<HotTipPolicyBody> }) {
  const { t } = useTranslation('assistantCompact');
  const regime = d.placement_regime || 'beginning_during';
  const custom = regime === 'custom';
  const asks = resolveAsks(d);
  const ax = axisMax(d);
  const bars = new Map<number, number>(); // bar → first turn in it
  asks.forEach((a) => { const b = barOf(a.pos); if (!bars.has(b)) bars.set(b, a.turn); });
  const regKey = REGIMES.find((r) => r.id === regime)?.key ?? 'startDuring';
  const edit = (i: number, p: Partial<{ turn: number; price: number | null; say: boolean }>) =>
    set(writeAsks(asks.map((a, k) => (k === i ? { ...a, ...p } : a)), regime));
  const floorPh = d.tip_min ? `$${d.tip_min}` : '—';
  const previewHelp = custom ? (asks.length ? t('mon.hot.askPointsHelp', { max: MAX_ASKS }) : t('mon.hot.noAsks')) : t('mon.hot.previewHelp');

  return (
    <Section title={t('mon.hot.placementTitle')} hint={t('mon.hot.placementHint')}>
      <Card>
        <Row label={t('mon.hot.placement')} help={t(`mon.hot.pl.${regKey}Help`)}>
          <Seg value={regime} onChange={(r) => set(setRegime(d, r))} options={REGIMES.map((r) => ({ id: r.id, label: t(`mon.hot.pl.${r.key}`) }))} />
        </Row>
        {isCountPreset(regime) && (
          <Row label={t('mon.hot.askCount')} help={t('mon.hot.askCountHelp')}>
            <NumInput value={asks.length} onChange={(v) => v !== null && set(setAskCount(d, v))} min={1} max={MAX_ASKS} int width={48} />
          </Row>
        )}
        <Row
          label={custom ? t('mon.hot.askPoints') : t('mon.hot.preview')}
          help={previewHelp}
          helpColor={custom && !asks.length ? C.purplePale : undefined}
        >
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
            {Array.from({ length: PREVIEW_BARS }, (_, k) => {
              const on = bars.has(k);
              const turn = on ? bars.get(k)! : barTurn(k, ax);
              const full = custom && !on && asks.length >= MAX_ASKS;
              return (
                <div key={k} style={{ width: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <div style={{ height: 40, display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      type="button"
                      disabled={!custom || full}
                      onClick={custom ? () => set(toggleBar(d, k)) : undefined}
                      title={t('mon.hot.barTip', { turn }) + (custom ? ' · ' + (on ? t('mon.hot.barRemove') : full ? t('mon.hot.barFull', { max: MAX_ASKS }) : t('mon.hot.barAdd')) : '')}
                      style={{ width: 18, height: 10 + k * 3, padding: 0, borderRadius: 3, border: `1px solid ${custom && !on ? 'var(--om-border-strong)' : 'transparent'}`, background: on ? C.purple : C.track, cursor: custom && !full ? 'pointer' : 'default' }}
                    />
                  </div>
                  <span style={{ height: 12, font: font(600, 9.5), color: C.purplePale, whiteSpace: 'nowrap' }}>{on ? `t${turn}` : ''}</span>
                </div>
              );
            })}
          </div>
        </Row>
        <div>
          <THead cols={ASK_COLS}><span>{t('mon.hot.colAsk')}</span><span>{t('mon.hot.colTurn')}</span><span>{t('mon.hot.colWhen')}</span><span>{t('mon.hot.colPrice')}</span><span>{t('mon.hot.colSay')}</span></THead>
          {asks.map((a, i) => (
            <div key={i} style={{ borderTop: `1px solid ${C.divider}` }}>
              <TRow cols={ASK_COLS} height={40}>
                <span style={{ font: font(500, 12.5), color: C.text, whiteSpace: 'nowrap' }}>{t('mon.hot.askN', { n: i + 1 })}</span>
                {custom ? (
                  <NumInput value={a.turn} onChange={(v) => v !== null && edit(i, { turn: v })} min={MIN_ASK_TURN} max={999} int width={48} />
                ) : (
                  <span style={{ font: font(600, 12), color: C.purplePale, whiteSpace: 'nowrap' }}>{`t${a.turn}`}</span>
                )}
                <span style={{ font: font(400, 12), color: C.sub }}>{t(`mon.hot.phase.${phaseOf(a.pos)}`)}</span>
                <NumInput value={a.price} onChange={(v) => edit(i, { price: v })} min={0} max={9999} allowEmpty placeholder={floorPh} width="100%" />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Toggle on={a.say} onChange={(v) => edit(i, { say: v })} />
                  <span style={{ font: font(400, 12), color: a.say ? C.text : C.muted, whiteSpace: 'nowrap' }}>{a.say ? t('mon.hot.sayPrice') : t('mon.hot.hintOnly')}</span>
                </div>
              </TRow>
            </div>
          ))}
        </div>
        <Foot>{t('mon.hot.sayNote')}</Foot>
      </Card>
    </Section>
  );
}

export function HotPage({ d, set, adv, togAdv }: PageProps<HotTipPolicyBody>) {
  const { t } = useTranslation('assistantCompact');
  const { t: tt } = useTranslation('tips');
  const endOnly = d.placement_regime === 'beginning_only';
  return (
    <Stack>
      <Placement d={d} set={set} />
      <AdvancedToggle open={adv} onToggle={togAdv} hint={t('mon.advHint.tip')} />
      {adv && (
        <>
          <HooksSection hooks={d.hook_styles} onChange={(h) => set({ hook_styles: h })} />
          <Section title={t('mon.pacing')} hint={t('mon.pacingHint')}>
            <Card>
              <Row label={t('mon.minTip')} help={d.allow_more ? t('mon.nudgeOn') : t('mon.nudgeOff')}>
                <span>$</span><NumInput value={d.tip_min} onChange={(v) => set({ tip_min: v })} min={0} max={9999} allowEmpty />
                <span style={{ marginLeft: 6 }}>{t('mon.nudgeAbove')}</span><Toggle on={d.allow_more} onChange={(v) => set({ allow_more: v })} />
              </Row>
              <Row label={t('mon.hot.askGap')} help={t('mon.hot.askGapHelp')}>
                <Turns><NumInput value={d.min_turns_between_asks} onChange={(v) => v !== null && set({ min_turns_between_asks: v })} min={0} max={99} int /></Turns>
              </Row>
              <Row label={t('mon.hot.arcSpan')} help={t('mon.hot.arcSpanHelp')}>
                <Turns><NumInput value={d.arc_span_turns} onChange={(v) => v !== null && set(setArcSpan(d, v))} min={1} max={999} int /></Turns>
              </Row>
            </Card>
          </Section>
          <Section title={t('mon.nego')} hint={t('mon.negoHint')}>
            <Card>
              <Row label={t('mon.maxAttempts')} help={t('mon.maxAttemptsHelp')}><NumInput value={d.max_negotiation_attempts} onChange={(v) => v !== null && set({ max_negotiation_attempts: v })} min={0} max={20} int /></Row>
              <Row label={t('mon.priceFloor')} help={t('mon.priceFloorHelp')}><NumInput value={pct(d.tip_drop_floor_pct)} onChange={(v) => v !== null && set({ tip_drop_floor_pct: v / 100 })} min={0} max={100} int /><span>%</span></Row>
              <Row label={t('mon.onMax')} help={t('mon.hot.onMaxHelp')}>
                <Select value={d.on_max_negotiations === 'ease' ? 'skip' : d.on_max_negotiations} onChange={(v) => set({ on_max_negotiations: v })} width={260} options={[
                  { id: 'skip', label: tt('onMax.keepSexting') }, { id: 'exit', label: tt('onMax.handOff') },
                ]} />
              </Row>
              <Row label={t('mon.grace')} help={t('mon.hot.graceHelp')}><NumInput value={d.negotiation_grace_turns} onChange={(v) => v !== null && set({ negotiation_grace_turns: v })} min={0} max={20} int /></Row>
              <GuidanceRow label={t('mon.negoGuidance')} value={d.negotiation_prompt} onChange={(v) => set({ negotiation_prompt: v })} placeholder={t('mon.negoGuidancePh')} />
            </Card>
          </Section>
          <AlertsSection
            decline={d.notify_on_decline} done={d.notify_on_completion}
            onDecline={(v) => set({ notify_on_decline: v })} onDone={(v) => set({ notify_on_completion: v })}
            doneLabel={t('mon.notifyTip')} doneHelp={t('mon.notifyTipHelp')}
            doneDisabledHelp={endOnly ? t('mon.hot.notifyTipOff') : undefined}
          />
          <Section title={t('mon.prompts')} hint={t('mon.promptsHint')}>
            <Card>
              <GuidanceRow label={t('mon.strategyPrompt')} hint={t('mon.layered')} value={d.strategy_prompt} onChange={(v) => set({ strategy_prompt: v })} placeholder={t('mon.strategyPh', { name: t('mon.s.hot').toLowerCase() })} height={96} />
            </Card>
          </Section>
        </>
      )}
    </Stack>
  );
}

/* ── Normal tip ─────────────────────────────────────────────────── */

export function NormalPage({ d, set, adv, togAdv }: PageProps<NormalTipPolicyBody>) {
  const { t } = useTranslation('assistantCompact');
  const { t: tt } = useTranslation('tips');
  return (
    <Stack>
      <Card>
        <Row label={t('mon.minTip')} help={t('mon.norm.minTipHelp')}><span>$</span><NumInput value={d.tip_min} onChange={(v) => set({ tip_min: v })} min={0} max={9999} allowEmpty /></Row>
        <Row label={t('mon.norm.state')} help={d.disclose_amount ? t('mon.norm.stateOn') : t('mon.norm.stateOff')}><Toggle on={d.disclose_amount} onChange={(v) => set({ disclose_amount: v })} /></Row>
        <Row label={t('mon.warmup')} help={t('mon.norm.warmupHelp')}><Turns><NumInput value={d.chatting_trigger_turns} onChange={(v) => v !== null && set({ chatting_trigger_turns: v })} min={0} max={999} int /></Turns></Row>
      </Card>
      <AdvancedToggle open={adv} onToggle={togAdv} hint={t('mon.advHint.normal')} />
      {adv && (
        <>
          <HooksSection hooks={d.hook_styles} onChange={(h) => set({ hook_styles: h })} />
          <Section title={t('mon.pacing')} hint={t('mon.pacingHint')}>
            <Card>
              <Row label={t('mon.norm.nudge')} help={d.allow_more ? t('mon.nudgeOn') : t('mon.nudgeOff')}><Toggle on={d.allow_more} onChange={(v) => set({ allow_more: v })} /></Row>
              <Row label={t('mon.minBetween')} help={t('mon.norm.minBetweenHelp')}><Turns><NumInput value={d.min_turns_between_asks} onChange={(v) => v !== null && set({ min_turns_between_asks: v })} min={0} max={99} int /></Turns></Row>
              <Row label={t('mon.askCaps')} help={t('mon.askCapsHelp')}>
                <span>{t('mon.norm.chatting')}</span><NumInput width={48} value={d.chatting_cap} onChange={(v) => v !== null && set({ chatting_cap: v })} min={0} max={99} int />
                <span style={{ marginLeft: 6 }}>{t('mon.overall')}</span><NumInput width={48} value={d.overall_cap} onChange={(v) => v !== null && set({ overall_cap: v })} min={0} max={99} int />
              </Row>
            </Card>
          </Section>
          <Section title={t('mon.nego')} hint={t('mon.negoHint')}>
            <Card>
              <Row label={t('mon.maxAttempts')} help={t('mon.maxAttemptsHelp')}><NumInput value={d.max_negotiation_attempts} onChange={(v) => v !== null && set({ max_negotiation_attempts: v })} min={0} max={20} int /></Row>
              <Row label={t('mon.priceFloor')} help={t('mon.priceFloorHelp')}><NumInput value={pct(d.tip_drop_floor_pct)} onChange={(v) => v !== null && set({ tip_drop_floor_pct: v / 100 })} min={0} max={100} int /><span>%</span></Row>
              <Row label={t('mon.onMax')} help={t('mon.onMaxHelp')}>
                <Select value={d.on_max_negotiations === 'ease' ? 'skip' : d.on_max_negotiations} onChange={(v) => set({ on_max_negotiations: v })} width={220} options={[
                  { id: 'skip', label: tt('onMax.continueChatting') }, { id: 'exit', label: tt('onMax.stopNotify') },
                ]} />
              </Row>
              <Row label={t('mon.grace')} help={t('mon.graceHelp')}><NumInput value={d.negotiation_grace_turns} onChange={(v) => v !== null && set({ negotiation_grace_turns: v })} min={0} max={20} int /></Row>
              <Row label={t('mon.maxTurns')} help={t('mon.maxTurnsHelp')}><NumInput value={d.max_negotiation_turns} onChange={(v) => v !== null && set({ max_negotiation_turns: v })} min={0} max={20} int /></Row>
              <GuidanceRow label={t('mon.negoGuidance')} value={d.negotiation_prompt} onChange={(v) => set({ negotiation_prompt: v })} placeholder={t('mon.negoGuidancePh')} />
            </Card>
          </Section>
          <AlertsSection
            decline={d.notify_on_decline} done={d.notify_on_completion}
            onDecline={(v) => set({ notify_on_decline: v })} onDone={(v) => set({ notify_on_completion: v })}
            doneLabel={t('mon.notifyTip')} doneHelp={t('mon.notifyTipHelp')}
          />
          <Section title={t('mon.prompts')} hint={t('mon.promptsHint')}>
            <Card>
              <GuidanceRow label={t('mon.norm.when')} hint={t('mon.whenHint')} value={d.propose_conditions} onChange={(v) => set({ propose_conditions: v })} placeholder={t('mon.norm.whenPh')} height={72} />
              <GuidanceRow label={t('mon.strategyPrompt')} hint={t('mon.layered')} value={d.nudge_strategy} onChange={(v) => set({ nudge_strategy: v })} placeholder={t('mon.strategyPh', { name: t('mon.s.normal').toLowerCase() })} height={96} />
            </Card>
          </Section>
        </>
      )}
    </Stack>
  );
}

/* ── VIP offer & Paid plan (persuasion only) ────────────────────── */

type Offer = VipOfferPolicyBody | SubOfferPolicyBody;

function OfferAdvanced<T extends Offer>({ d, set, plan, children }: { d: T; set: Patch<T>; plan: boolean; children?: ReactNode }) {
  const { t } = useTranslation('assistantCompact');
  const { t: tt } = useTranslation('tips');
  const s = set as Patch<Offer>;
  return (
    <>
      <HooksSection hooks={d.hook_styles} onChange={(h) => s({ hook_styles: h })} invite />
      <Section title={t('mon.pacing')} hint={t('mon.offer.pacingHint')}>
        <Card>
          <Row label={t('mon.minBetween')} help={t('mon.offer.minBetweenHelp')}><Turns><NumInput value={d.min_turns_between_asks} onChange={(v) => v !== null && s({ min_turns_between_asks: v })} min={0} max={999} int /></Turns></Row>
          <Row label={t('mon.offer.caps')} help={t('mon.offer.capsHelp')}>
            <span>{t('mon.offer.session')}</span><NumInput width={48} value={d.max_offers_per_session} onChange={(v) => v !== null && s({ max_offers_per_session: v })} min={0} max={99} int />
            <span style={{ marginLeft: 6 }}>{t('mon.offer.lifetime')}</span><NumInput width={48} value={d.max_offers_lifetime} onChange={(v) => v !== null && s({ max_offers_lifetime: v })} min={0} max={99} int />
          </Row>
          <Row label={t('mon.offer.combined')} help={plan ? t('mon.offer.combinedHelpPlan') : t('mon.offer.combinedHelp')}><NumInput value={d.combined_session_cap} onChange={(v) => v !== null && s({ combined_session_cap: v })} min={0} max={99} int /></Row>
          <Row label={t('mon.offer.sinceTip')} help={plan ? t('mon.offer.sinceTipHelpPlan') : t('mon.offer.sinceTipHelp')}><Turns><NumInput value={d.min_turns_since_last_tip} onChange={(v) => v !== null && s({ min_turns_since_last_tip: v })} min={0} max={999} int /></Turns></Row>
          <Row label={t('mon.offer.never')} help={t('mon.offer.neverHelp')}><Toggle on={d.block_after_decline} onChange={(v) => s({ block_after_decline: v })} /></Row>
        </Card>
      </Section>
      <Section title={t('mon.nego')} hint={t('mon.negoHint')}>
        <Card>
          <Row label={t('mon.offer.rebuttals')} help={t('mon.offer.rebuttalsHelp')}><NumInput value={d.max_negotiation_attempts} onChange={(v) => v !== null && s({ max_negotiation_attempts: v })} min={0} max={20} int /></Row>
          <Row label={t('mon.onMax')} help={t('mon.onMaxHelp')}>
            <Select value={d.on_max_negotiations} onChange={(v) => s({ on_max_negotiations: v })} width={260} options={[
              { id: 'ease', label: tt('onMax.easeStandDown') }, { id: 'exit', label: tt('onMax.exitBackOff') },
            ]} />
          </Row>
          <Row label={t('mon.grace')} help={t('mon.graceHelp')}><NumInput value={d.negotiation_grace_turns} onChange={(v) => v !== null && s({ negotiation_grace_turns: v })} min={0} max={20} int /></Row>
          <Row label={t('mon.maxTurns')} help={t('mon.maxTurnsHelp')}><NumInput value={d.max_negotiation_turns} onChange={(v) => v !== null && s({ max_negotiation_turns: v })} min={0} max={20} int /></Row>
          <GuidanceRow label={t('mon.negoGuidance')} value={d.negotiation_prompt} onChange={(v) => s({ negotiation_prompt: v })} placeholder={plan ? t('mon.offer.negoPhPlan') : t('mon.offer.negoPhVip')} />
        </Card>
      </Section>
      <AlertsSection
        decline={d.notify_on_decline} done={d.notify_on_completion}
        onDecline={(v) => s({ notify_on_decline: v })} onDone={(v) => s({ notify_on_completion: v })}
        doneLabel={plan ? t('mon.offer.notifyUpgrade') : t('mon.offer.notifyJoin')} doneHelp={plan ? t('mon.offer.notifyUpgradeHelp') : t('mon.offer.notifyJoinHelp')}
      />
      <Section title={t('mon.prompts')} hint={t('mon.promptsHint')}>
        <Card>
          <GuidanceRow label={plan ? t('mon.offer.whenPlan') : t('mon.offer.whenVip')} hint={t('mon.whenHint')} value={d.propose_conditions} onChange={(v) => s({ propose_conditions: v })} placeholder={t('mon.offer.whenPh')} height={72} />
          {children}
        </Card>
      </Section>
    </>
  );
}

export function VipPage({ d, set, adv, togAdv }: PageProps<VipOfferPolicyBody>) {
  const { t } = useTranslation('assistantCompact');
  return (
    <Stack>
      <Card>
        <Row label={t('mon.vip.url')} help={t('mon.vip.urlHelp')}><TextInput value={d.page_url ?? ''} onChange={(v) => set({ page_url: v })} placeholder="https://onlyfans.com/her_vip" width={280} /></Row>
        <Row label={t('mon.vip.price')} help={t('mon.vip.priceHelp')}><span>$</span><NumInput value={d.page_price} onChange={(v) => set({ page_price: v })} min={0} max={9999} allowEmpty placeholder={t('none')} width={64} /></Row>
        <Row label={t('mon.warmup')} help={t('mon.vip.warmupHelp')}><Turns><NumInput value={d.trigger_turns} onChange={(v) => v !== null && set({ trigger_turns: v })} min={0} max={999} int /></Turns></Row>
      </Card>
      <AdvancedToggle open={adv} onToggle={togAdv} hint={t('mon.advHint.tip')} />
      {adv && (
        <OfferAdvanced d={d} set={set} plan={false}>
          <GuidanceRow label={t('mon.offer.strategy')} hint={t('mon.layered')} value={d.offer_strategy} onChange={(v) => set({ offer_strategy: v })} placeholder={t('mon.strategyPh', { name: t('mon.s.vip').toLowerCase() })} height={96} />
        </OfferAdvanced>
      )}
    </Stack>
  );
}

function InstructionPhotos({ accountId, ids, onChange }: { accountId: string; ids: string[]; onChange: (ids: string[]) => void }) {
  const { t } = useTranslation('assistantCompact');
  const cat = useSalesCatalogue(accountId);
  const [open, setOpen] = useState(false);
  const resolving = useResolveIds(accountId, cat, ids);
  return (
    <Row label={t('mon.sub.photos')} help={t('mon.sub.photosHelp')}>
      {ids.length ? (
        <>
          <ThumbStrip accountId={accountId} ids={ids} byId={cat.byId} loading={resolving} />
          <span style={{ color: C.sub }}>{t('picker.photos', { count: ids.length })}</span>
        </>
      ) : (
        <span>{t('mon.sub.noPhotos')}</span>
      )}
      <Btn kind="ghost" icon="image" onClick={() => setOpen(true)} style={{ color: C.purplePale, marginLeft: 4 }}>{ids.length ? t('mon.sub.editPhotos') : t('mon.sub.addPhotos')}</Btn>
      {open && (
        <MediaPicker
          accountId={accountId}
          title={t('mon.sub.pickTitle')}
          initialSelected={ids}
          initialType="photo"
          allowEmpty
          fit={(_, v) => (v ? { text: t('mon.sub.pickVideos'), tone: 'warn' } : { text: t('mon.sub.pickHint'), tone: 'ok' })}
          onConfirm={(sel, items) => {
            const vid = new Set(items.filter((i) => isVideoItem(i.type)).map((i) => i.id));
            onChange(sel.filter((x) => !vid.has(x)));
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </Row>
  );
}

export function SubPage({ accountId, d, set, adv, togAdv }: PageProps<SubOfferPolicyBody> & { accountId: string }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <Stack>
      <Card>
        <Row stack label={t('mon.sub.brief')} help={t('mon.sub.briefHelp')}>
          <TextArea value={d.offer_brief ?? ''} onChange={(v) => set({ offer_brief: v })} height={110} />
        </Row>
        <InstructionPhotos accountId={accountId} ids={d.instruction_content_ids ?? []} onChange={(ids) => set({ instruction_content_ids: ids })} />
        <Row label={t('mon.warmup')} help={t('mon.sub.warmupHelp')}><Turns><NumInput value={d.trigger_turns} onChange={(v) => v !== null && set({ trigger_turns: v })} min={0} max={999} int /></Turns></Row>
      </Card>
      <AdvancedToggle open={adv} onToggle={togAdv} hint={t('mon.advHint.tip')} />
      {adv && <OfferAdvanced d={d} set={set} plan />}
    </Stack>
  );
}
